import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import logger from '../utils/logger';
// import { identifyUser } from '@propriety/analytics';
import {
  authService,
  AUTH_ERROR_CODES,
  consumePersistedLinkInviteAuthError,
  clearPersistedLinkInviteAuthError,
  isLinkInviteRevokedError,
  persistLinkInviteAuthError,
} from '../services/authService';
import { getSigninURLWithAllParams } from '../utils/urlUtils';
import { useStableNavigate } from '../common/StableRouterContextProvider';
import {
  syncAuthPollToServiceWorker,
  stopAuthPollFromServiceWorker,
  clearAuthPollSuppression,
} from '../service_worker/client';
import { toast } from '../common/toast';
import { isDevelopmentEnvironment } from '../utils/townViewDevMode';
import { getApiUrl } from '../utils/config';

const UserContext = createContext();

const historicalViewUsers = ['brennan@aventine.ai',
  //  'jrburns@aventine.ai'
  ]; // List of users who can view historical data

// All of this should eventually be moved into either a backend, or a DB
// at the time of multi user tenancy (beyond aventine) this needs to be updated into our backend validation logic
// Export the userObject
export const userObject = {
  'james@aventine.ai': { userId: 1, access: {level:'admin',years:null}, userName: 'James', fullName: 'James Burns', organization: 'Aventine Properties' },
  // Operational admin user example
  'eileen@aventine.ai': { userId: 2, access: {level:'OperationalAdmin',years:null}, userName: 'Eileen', organization: 'Aventine Properties' },
  'oscar@aventine.ai': { userId: 4, access: {level:'aventine',years:null}, userName: 'Oscar', organization: 'Aventine Properties' },
  
  // setup as "analyst, or developer" role that lets peter access the data cleaning stuff
  'peter@aventine.ai': { userId: 5, access: {level:'aventine',years:null}, userName: 'Peter', organization: 'Aventine Properties' },
  'misbah@aventine.ai': { userId: 10, access: {level:'aventine',years:null}, userName: 'Misbah', organization: 'Aventine Properties' },
  'rogendy@aventine.ai': { userId: 12, access: {level:'aventine',years:null}, userName: 'Rogendy', organization: 'Aventine Properties' },
  'jrbmisand@optonline.net': { userId: 13, access: {level:'admin',years:null}, userName: 'Jim', organization: 'Propriety' },

  // The propriety users
  'jrburns@aventine.ai': { userId: 16, access: {level:'propadmin',years:null}, userName: 'James Jr', fullName: 'James Burns', organization: 'Propriety' },
  'brennan@aventine.ai': { userId: 19, access: {level:'propadmin',years:null}, userName: 'Brennan', organization: 'Autism Unlimited LLC' },

  // The aventine training users
  'max@aventine.ai': { userId: 14, access: {level:'Training',years:null}, userName: 'Max', organization: 'Aventine Properties' },
  'lissette@aventine.ai': { userId: 20, access: {level:'Training',years:null}, userName: 'Lissette', organization: 'Aventine Properties' },
  'supandeep@aventine.ai': { userId: 21, access: {level:'Training',years:null}, userName: 'Supy', organization: 'Aventine Properties' },
  
  // Deprecated but keeping to display their name in grids for now.
  'christina@aventine.ai': { userId: 22, access: {level:'aventine',years:null}, userName: 'Christina', organization: 'Aventine Properties' },
  'alex@aventine.ai': { userId: 23, access: {level:'Training',years:null}, userName: 'Alex', organization: 'Aventine Properties' },
  
  'rip@aventine.ai': { userId: 24, access: {level:'OperationalAdmin',years:null}, userName: 'Rip', organization: 'Aventine Properties' },
  'brooke@aventine.ai': { userId: 25, access: {level:'aventine',years:null}, userName: 'Brooke', organization: 'Aventine Properties' },
  'chris@aventine.ai': { userId: 26, access: {level:'Training',years:null}, userName: 'Chris', organization: 'Aventine Properties' },
  'catherine@aventine.ai': { userId: 27, access: {level:'Training',years:null}, userName: 'Catherine', organization: 'Aventine Properties' },
  'cathy@aventine.ai': { userId: 28, access: {level:'aventine',years:null}, userName: 'Cathy', organization: 'Aventine Properties' },
  'tom@aventine.ai': { userId: 29, access: {level:'aventine',years:null}, userName: 'Tom', organization: 'Aventine Properties' },
  'manny@aventine.ai': { userId: 30, access: {level:'Training',years:null}, userName: 'Manny', organization: 'Aventine Properties' },
  'scott@aventine.ai': { userId: 31, access: {level:'aventine',years:null}, userName: 'Scott', organization: 'Aventine Properties' },
  'darlene@aventine.ai': { userId: 32, access: {level:'Training',years:null}, userName: 'Darlene', organization: 'Aventine Properties' },
  'gianna@aventine.ai': { userId: 33, access: {level:'Training',years:null}, userName: 'Gianna', organization: 'Aventine Properties' },

  'jonathan@aventine.ai': { userId: 99, access: {level:'Training',years:null}, userName: 'Jonathan', organization: 'Aventine Properties' },

  // System user stamped on Town Portal actions.
  'townportal@aventine.ai': { userId: 109, access: {level:'Training',years:null}, userName: 'Town Portal', fullName: 'Town Portal', organization: 'Propriety' },
};

// Users who should see a warning before overwriting comps previously saved by that same user
export const WarnList = [
  1, // James Sr (Propriety)
  10, // Misbah (Aventine)
  24, // Rip (OperationalAdmin)
  22, // Christina (Aventine)
];

// Add role-based permission mapping and helpers
const ROLE_PERMS = {
  admin: {
    reports: ['standard', 'assessment', 'settlement', 'marketing', 'data_analysis', 'evidence_comparison'],
    runTypes: ['all_clients', 'court_date', 'single_parcel', 'rep_id'],
  },
  propadmin: {
    reports: ['standard', 'assessment', 'settlement', 'marketing', 'data_analysis', 'evidence_comparison'],
    runTypes: ['all_clients', 'court_date', 'single_parcel', 'rep_id'],
  },
  // add a case review admin who can save default filters?
  OperationalAdmin: {
    reports: ['standard', 'assessment', 'data_analysis', 'evidence_comparison'],
    runTypes: ['all_clients', 'court_date', 'single_parcel', 'rep_id'],
  },
  aventine: {
    reports: ['standard', 'assessment', 'data_analysis', 'evidence_comparison'],
    runTypes: ['all_clients', 'court_date', 'single_parcel', 'rep_id'],
  },
  Training: {
    reports: ['standard', 'assessment', 'data_analysis'],
    runTypes: ['all_clients', 'court_date', 'single_parcel', 'rep_id'],
  },
};

// Add feature-based permission mapping and helpers
const ROLE_FEATURES = {
  admin: ['update_weights', 'visualization_tool', 'task_creation', 'update_case_status', 'email_offers', 'cancel_offers'],
  propadmin: ['update_weights', 'visualization_tool', 'task_creation', 'update_case_status', 'email_offers', 'cancel_offers'],
  OperationalAdmin: ['visualization_tool', 'task_creation', 'update_case_status', 'email_offers', 'cancel_offers'],
  aventine: ['update_case_status', 'email_offers', 'cancel_offers'],
  Training: [],
};

const canRunReportHelper = (level, report) =>
  !!ROLE_PERMS[level]?.reports.includes(report);

const canUseRunTypeHelper = (level, runType) =>
  !!ROLE_PERMS[level]?.runTypes.includes(runType);

const canUseFeatureHelper = (level, feature) =>
  !!ROLE_FEATURES[level]?.includes(feature);

// W4600-000-000-09820-084
export const isUserAuthorized = (email, organization) => {
  // Check if the email exists in the userObject
  if (userObject[email]) {
    // Optionally, add organization-specific checks here if needed
    return true;
  }
  return false;
};

export const TOWN_PORTAL_USER_ID = 109;

export const createUserInfo = (email, isTownView = true) => {
  if (!userObject[email]) {
    return {
      email,
      userId: isTownView ? TOWN_PORTAL_USER_ID : null,
      userAccess: false,
      userName: null,
      organization: null,
      viewHistorical: historicalViewUsers.includes(email),
      townView: isTownView,
      // Default feature flags as binary values
      manualReview: 1,
      showSettledCases: 1,
      reviewedCasesFlag: 1,
      manualReviewOverride: 0,
      scarOnly: 0,
    };
  }

  const { userId, access, userName, organization, slackId, userPhone } = userObject[email];

  return {
    email,
    userId: userId,
    userAccess: access,
    userName,
    organization,
    ...('slackId' in userObject[email] ? { slackId: slackId || null, userPhone: userPhone || null } : {}),
    viewHistorical: historicalViewUsers.includes(email),
    townView: isTownView,
    // Default feature flags as binary values
    manualReview: 1,
    showSettledCases: 1,
    reviewedCasesFlag: 1,
    manualReviewOverride: 0,
    scarOnly: 0,
  };
};

// Function to clear recent session errors (to be called on successful login)
export const clearRecentSessionErrors = () => {
  // This will be implemented in axiosConfig.js
  if (window.clearRecentSessionErrors) {
    window.clearRecentSessionErrors();
  }
};

// Helper function to get user name by ID
export const getUserNameById = (userId) => {
  const normalized = userId != null ? String(userId).trim() : '';
  if (!normalized || normalized === '_') return 'Unassigned';
  const userEntry = Object.values(userObject).find(u => String(u.userId) === normalized);
  return userEntry ? userEntry.userName : `User ${userId}`;
};

/** Email for a Propriety user id (from userObject); null if unknown. */
export const getUserEmailById = (userId) => {
  const normalized = userId != null ? String(userId).trim() : '';
  if (!normalized || normalized === '_') return null;
  const entry = Object.entries(userObject).find(([, u]) => String(u.userId) === normalized);
  return entry ? entry[0] : null;
};

export const UserProvider = ({ children }) => {
  const navigate = useStableNavigate();
  const [userState, setUserState] = useState(() => {
    try {
      if (
        typeof window !== 'undefined' &&
        window.location.pathname.includes('/signin')
      ) {
        return null;
      }
      const params = new URLSearchParams(
        typeof window !== 'undefined' ? window.location.search : ''
      );
      if (params.has('magic_token')) {
        return null;
      }
      const storedUser = authService.getLinkUserInfo() || localStorage.getItem('userInfo');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      try { sessionStorage.removeItem('linkUserInfo'); } catch {}
      localStorage.removeItem('userInfo');
      return null;
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(() =>
    typeof window !== 'undefined' ? consumePersistedLinkInviteAuthError() : null
  );
  const isMounted = useRef(true);
  const authAttempted = useRef(false);
  const contactsLoaded = useRef(false);

  useEffect(() => {
    if (loading || !userState?.email || userState.isLinkAuthenticated || contactsLoaded.current) return undefined;
    contactsLoaded.current = true;
    let alive = true;
    const token = authService.getActiveSessionToken();
    fetch(getApiUrl('/identify_user/map'), {
      headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }).then((res) => {
      if (!res.ok) throw new Error(`identify_user/map ${res.status}`);
      return res.json();
    }).then((data) => {
      Object.entries(data?.userObject || {}).forEach(([email, row]) => {
        const local = userObject[email];
        if (!local) return;
        local.slackId = row.slackId || null;
        local.userPhone = row.userPhone || null;
      });
      if (!alive) return;
      setUserState((prev) => {
        if (!prev?.email || prev.isLinkAuthenticated) return prev;
        const row = userObject[prev.email];
        if (!row) return prev;
        return { ...prev, slackId: row.slackId || null, userPhone: row.userPhone || null };
      });
    }).catch((error) => {
      logger.error('Could not load Slack IDs from Users', error);
    });
    return () => { alive = false; };
  }, [loading, userState?.email, userState?.isLinkAuthenticated]);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (userState && authService.getActiveSessionToken()) {
      syncAuthPollToServiceWorker();
    } else {
      stopAuthPollFromServiceWorker({ suppress: false });
    }
  }, [userState, loading]);

  useEffect(() => {
    const initializeAuth = async () => {
      // Prevent multiple authentication attempts
      if (authAttempted.current) {
        return;
      }
      authAttempted.current = true;
      
      try {
        // Use the authentication service
        const authResult = await authService.handleAutoAuthentication();
        // logger.log('auth result from auto auth', authResult)
        
        if (authResult && isMounted.current) {
          setUserState(authResult.userInfo);
          if (authResult?.userInfo?.isLinkAuthenticated) {
            authService.setLinkUserInfo(authResult.userInfo);
          } else {
            localStorage.setItem('userInfo', JSON.stringify(authResult.userInfo));
          }
          
          if (authResult.userInfo.userId) {
            // identifyUser(authResult.userInfo.userId, authResult.userInfo);
          }

          // Store the redirect path for the SignIn component to handle
          // Don't redirect here - let React Router handle it smoothly
        } else {
          logger.log('🚀 UserContext: No valid JWT session, redirecting to signin...');
          
          // Check if we're already on signin page with an error parameter
          const isOnSigninWithError = window.location.pathname.includes('/signin') && 
                                     new URLSearchParams(window.location.search).has('error');
          
          const isLinkInviteFailure =
            authError === AUTH_ERROR_CODES.LINK_INVITE_REVOKED;
          if (!isOnSigninWithError && !isLinkInviteFailure) {
            toast.error('Session expired, please sign in again');
          }
          
          // No valid JWT session, redirect to signin while preserving URL parameters
          if (window.location.pathname === '/snap') {
            try { sessionStorage.setItem('authRedirectPath', '/snap'); } catch {}
          }
          if (!window.location.pathname.includes('/signin')) {
            window.location.href = getSigninURLWithAllParams();
          }
        }
      } catch (error) {
        logger.error('Auth initialization error:', error);
        authService.presentAuthFailure(error);
        setUserState(null);
        try { sessionStorage.removeItem('linkUserInfo'); } catch {}
        localStorage.removeItem('userInfo');
        if (isLinkInviteRevokedError(error)) {
          persistLinkInviteAuthError();
          setAuthError(error.code);
          if (window.location.pathname === '/snap') {
            try { sessionStorage.setItem('authRedirectPath', '/snap'); } catch {}
          }
          if (!window.location.pathname.includes('/signin')) {
            navigate(getSigninURLWithAllParams(), { replace: true });
          }
        } else {
          setAuthError(error.message);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth bootstrap once; authError is read only for toast guard
  }, [navigate]);

  const login = useCallback(async (userData) => {
    localStorage.setItem('userInfo', JSON.stringify(userData));
    setUserState(userData);
    
    if (userData.userId) {
      logger.log('identifying user (disabled', userData.userId, userData)
      // identifyUser(userData.userId, userData);
    }
    
    clearPersistedLinkInviteAuthError();
    clearAuthPollSuppression();
    clearRecentSessionErrors();
  }, []);

  // Listen for logout events from authService
  useEffect(() => {
    const handleLogout = () => {
      setUserState(null);
      setAuthError(null);
      stopAuthPollFromServiceWorker({ suppress: false });
    };

    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  const logout = useCallback(() => {
    // Clear React state only
    setUserState(null);
    setAuthError(null);
    stopAuthPollFromServiceWorker({ suppress: false });
  }, []);

  // Memoize permission functions
  const canRunReport = useCallback((report) => 
    canRunReportHelper(userState?.userAccess?.level, report), 
    [userState?.userAccess?.level]
  );

  const canUseRunType = useCallback((runType) => 
    canUseRunTypeHelper(userState?.userAccess?.level, runType), 
    [userState?.userAccess?.level]
  );

  const canUseFeature = useCallback((feature) => 
    canUseFeatureHelper(userState?.userAccess?.level, feature), 
    [userState?.userAccess?.level]
  );

  // Town view: `user.townView`. Link permission flags (`shareWorkups`, etc.) are set only on link auth via `mapTokenPermissionsToUser`.
  const townView = Boolean(userState?.townView);

  // Dev-only Propriety admin preview. Not persisted, so a refresh restores the real session view.
  const canPreviewTownView = isDevelopmentEnvironment() && userState?.userAccess?.level === 'propadmin' && !userState?.isLinkAuthenticated;
  const setTownView = useCallback((next) => setUserState((prev) => prev && { ...prev, townView: next }), []);

  const contextValue = useMemo(
    () => ({
      user: userState,
      loading,
      authError,
      login,
      logout,
      townView,
      canPreviewTownView,
      setTownView,
      canRunReport,
      canUseRunType,
      canUseFeature,
      WarnList,
    }),
    [userState, loading, authError, login, logout, townView, canPreviewTownView, setTownView, canRunReport, canUseRunType, canUseFeature]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);